import { Component, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { startPoll } from "@/lib/poll";

export const REFRESH_EVENT = "console:refresh";
export const emitRefresh = () => window.dispatchEvent(new Event(REFRESH_EVENT));

export const fieldStyle = { background: "var(--bg-primary)", border: "1px solid var(--border)", color: "var(--text-primary)" };
export const cad = (n) => `$${Number(n || 0).toFixed(2)}`;
export const stamp = (iso) =>
  iso ? new Date(iso).toLocaleString("en-CA", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }) : "—";

export const LOAD_FAILED = { failed: true };
const failText = (e) => (e.response ? `server error ${e.response.status}` : "network error");

// data is null while loading, the payload when ok, or LOAD_FAILED if the first load fails (later failures keep the last good data)
export function useLoad(path, interval = 0) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    if (!path) return null;
    setData((d) => (d === LOAD_FAILED ? null : d));
    return api.get(path).then(({ data }) => setData(data)).catch((e) => {
      console.error("console load failed", path, e);
      setData((d) => (d === null || d === LOAD_FAILED ? { ...LOAD_FAILED, why: failText(e), retry: load } : d));
    });
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setData(null); // a new filter/path must not show the previous list
    load();
    window.addEventListener(REFRESH_EVENT, load);
    const stop = interval ? startPoll(load, interval) : null;
    return () => { window.removeEventListener(REFRESH_EVENT, load); stop?.(); };
  }, [load, interval]);
  return [data, load];
}

export function Panel({ title, right, children, testId, className = "" }) {
  return (
    <section className={`fade-in-up ${className}`} style={{ border: "1px solid var(--border)", background: "var(--bg-secondary)" }} data-testid={testId}>
      {title && (
        <header className="flex items-center justify-between gap-4 px-4 py-3" style={{ borderBottom: "1px solid var(--border)" }}>
          <h2 className="text-[11px] tracking-[0.25em] flex items-center gap-2" style={{ color: "var(--text-primary)" }}>
            <span style={{ color: "var(--accent)" }}>▌</span>{title}
          </h2>
          {right}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function Kpi({ label, value, sub, alert, onClick, testId }) {
  return (
    <button onClick={onClick} disabled={!onClick} data-testid={testId}
      className="text-left p-4 transition-colors duration-200 hover:bg-[var(--bg-primary)] disabled:cursor-default relative"
      style={{ border: `1px solid ${alert ? "var(--accent)" : "var(--border)"}`, background: "var(--bg-secondary)" }}>
      {alert && <span className="absolute top-3 right-3 w-2 h-2 animate-pulse" style={{ background: "var(--accent)" }} />}
      <span className="block text-[10px] tracking-[0.2em] opacity-70">{label}</span>
      <span className="block font-head text-2xl md:text-3xl mt-3 tracking-tight break-all" style={{ color: "var(--accent)" }} data-testid={testId && `${testId}-value`}>{value}</span>
      {sub && <span className="block text-[10px] tracking-widest mt-2 opacity-60">{sub}</span>}
    </button>
  );
}

export function Chip({ children, on, testId }) {
  return (
    <span className="inline-block text-[10px] tracking-widest px-2 py-[2px] whitespace-nowrap" data-testid={testId}
      style={{ border: "1px solid var(--accent)", background: on ? "var(--accent)" : "transparent", color: on ? "#121212" : "var(--accent)" }}>
      {children}
    </span>
  );
}

export function Filters({ options, value, onChange, testId }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {options.map(([k, label]) => (
        <button key={k} onClick={() => onChange(k)} data-testid={`${testId}-${k || "all"}`}
          className="text-[10px] tracking-widest px-3 py-1 transition-colors duration-200"
          style={{ border: `1px solid ${value === k ? "var(--accent)" : "var(--border)"}`, background: value === k ? "var(--accent)" : "transparent", color: value === k ? "#121212" : "var(--text-primary)" }}>
          {label}
        </button>
      ))}
    </div>
  );
}

export function Failed({ data, testId = "load-failed" }) {
  return (
    <div className="flex items-center gap-4 py-6 text-xs" data-testid={testId}>
      <span style={{ color: "var(--accent)" }}>could not load · {data.why}</span>
      <button onClick={() => data.retry?.()} data-testid={`${testId}-retry`} className="btn px-3 py-1 text-[10px] tracking-widest">retry</button>
    </div>
  );
}

// renders loading / failed states; returns null when data is ready
export function Gate({ data, testId }) {
  if (data === null || data === undefined) return <p className="text-xs opacity-60 py-6" data-testid={testId && `${testId}-loading`}>loading…</p>;
  if (data.failed) return <Failed data={data} testId={testId && `${testId}-failed`} />;
  return null;
}

export const ready = (d) => d && !d.failed;

export function Table({ cols, rows, rowKey = "id", onRow, empty = "nothing here", testId }) {
  if (!ready(rows)) return <Gate data={rows} testId={testId} />;
  if (!rows.length) return <p className="text-xs opacity-60 py-6" data-testid={`${testId}-empty`}>{empty}</p>;
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr>{cols.map((c) => <th key={c.k} className={`text-left font-normal text-[10px] tracking-[0.2em] opacity-60 py-2 px-3 whitespace-nowrap ${c.cls || ""}`} style={{ borderBottom: "1px solid var(--border)" }}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r[rowKey]} onClick={onRow ? () => onRow(r) : undefined} data-testid={`${testId}-row-${r[rowKey]}`}
              className={`transition-colors duration-150 ${onRow ? "cursor-pointer hover:bg-[var(--bg-primary)]" : ""}`} style={{ borderBottom: "1px solid var(--border)" }}>
              {cols.map((c) => <td key={c.k} className={`py-3 px-3 align-top ${c.cls || ""}`}>{c.render ? c.render(r) : r[c.k] ?? "—"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Modal({ title, onClose, children, testId, wide }) {
  // portal: animated panels create a containing block that would clip fixed overlays
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 fade-in" style={{ background: "rgba(0,0,0,0.8)" }} onClick={onClose} data-testid={`${testId}-overlay`}>
      <div className={`w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[88vh] overflow-y-auto`} style={{ background: "var(--bg-modal)", border: "1px solid var(--accent)" }} onClick={(e) => e.stopPropagation()} data-testid={testId}>
        <header className="flex justify-between items-center px-5 py-3 sticky top-0" style={{ borderBottom: "1px solid var(--border)", background: "var(--bg-modal)" }}>
          <span className="text-[11px] tracking-[0.25em]"><span style={{ color: "var(--accent)" }}>▌</span>{title}</span>
          <button onClick={onClose} data-testid={`${testId}-close`} className="text-xs opacity-60 hover:opacity-100">[ close ]</button>
        </header>
        <div className="p-5">{children}</div>
      </div>
    </div>,
    document.body
  );
}

// keeps one broken section from blanking the whole console
export class SectionBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error("console section crashed", error, info); }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="p-6 text-xs flex items-center gap-4" style={{ border: "1px solid var(--accent)" }} data-testid="section-crashed">
        <span style={{ color: "var(--accent)" }}>this section hit an error · {String(this.state.error.message || this.state.error).slice(0, 120)}</span>
        <button onClick={() => this.setState({ error: null })} data-testid="section-crashed-retry" className="btn px-3 py-1 text-[10px] tracking-widest">retry</button>
      </div>
    );
  }
}

export function Label({ children }) {
  return <span className="block text-[10px] tracking-[0.2em] opacity-60 mb-2">{children}</span>;
}
